// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = "https://xdhkcgssdbisyhgweaql.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhkaGtjZ3NzZGJpc3loZ3dlYXFsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzQwMjg4NjgsImV4cCI6MjA0OTYwNDg2OH0.lWeYbpX4PcpylMOchO4Rue8e352Yjeq8T5LDKF_MeqE";

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

/**
 * Resilient storage wrapper.
 * Writes to BOTH localStorage and sessionStorage so a session survives:
 * - tab reloads
 * - mobile orientation changes (which can recreate the WebView/page)
 * - PWA relaunches
 * Reads fall back to whichever store still has the token.
 */
const memoryStorage: Record<string, string> = {};

const resilientStorage = {
  getItem: (key: string): string | null => {
    let fromLocal: string | null = null;
    let fromSession: string | null = null;

    try { fromLocal = window.localStorage.getItem(key); } catch {}
    if (fromLocal) return fromLocal;

    try { fromSession = window.sessionStorage.getItem(key); } catch {}
    if (fromSession) {
      // Re-hydrate localStorage so future reads are fast, but do not fail if Canvas blocks it.
      try { window.localStorage.setItem(key, fromSession); } catch {}
      return fromSession;
    }

    if (memoryStorage[key]) return memoryStorage[key];

    return null;
  },
  setItem: (key: string, value: string): void => {
    memoryStorage[key] = value;
    try { window.localStorage.setItem(key, value); } catch {}
    try { window.sessionStorage.setItem(key, value); } catch {}
  },
  removeItem: (key: string): void => {
    delete memoryStorage[key];
    try { window.localStorage.removeItem(key); } catch {}
    try { window.sessionStorage.removeItem(key); } catch {}
  },
};

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: resilientStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'salesby-auth',
    flowType: 'pkce',
  },
});

/**
 * PERFORMANCE: `auth.getUser()` performs a network round-trip to /auth/v1/user
 * on every call. The app calls it dozens of times per page render, which added
 * seconds of latency on every navigation. We serve the user from the locally
 * stored (and auto-refreshed) session instead, and only fall back to the
 * network call when there is no local session.
 */
const originalGetUser = supabase.auth.getUser.bind(supabase.auth);
(supabase.auth as any).getUser = async (jwt?: string) => {
  if (jwt) return originalGetUser(jwt);
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      return { data: { user: session.user }, error: null } as any;
    }
  } catch {
    // fall through to the network call
  }
  return originalGetUser();
};

